import { FC, PropsWithChildren } from 'react';
import clsx from 'clsx';
import type { UseImageUrlBaseProps } from '../../hooks';
import { twMerge } from 'tailwind-merge';

type PitchBackgroundSize = 'large' | 'small';
type PitchBackgroundVariant = 'green' | 'grey' | 'aquamarine';

export interface PitchBackgroundProps extends Partial<UseImageUrlBaseProps> {
  className?: string;
  frame?: boolean;
  imageClassName?: string;
  largeImageClassName?: string;
  size?: PitchBackgroundSize;
  smallImageClassName?: string;
  variant?: PitchBackgroundVariant;
}

const getBackgroundFilename = ({
  variant,
  frame,
}: {
  variant: PitchBackgroundVariant | undefined;
  frame: boolean | undefined;
}) => {
  const filename = [variant, frame ? 'frame' : undefined].filter(v => !!v).join('-');
  return filename;
};

const BasePitchBackground: FC<PropsWithChildren<PitchBackgroundProps>> = ({
  className,
  size,
  frame,
  variant,
  children,
  imageClassName,
  largeImageClassName,
  smallImageClassName,
  hostname = 'https://engagecraftfantasydev.blob.core.windows.net',
  pathname = 'pitch-backgrounds',
  ext = 'png',
}) => {
  const filename = getBackgroundFilename({ variant, frame });
  const smallImageDisplayClassName =
    size === 'large' ? 'hidden' : size === 'small' ? 'block' : 'block md:hidden';
  const largeImageDisplayClassName =
    size === 'small' ? 'hidden' : size === 'large' ? 'block' : 'hidden md:block';

  return (
    <div className={clsx('relative overflow-hidden', className)}>
      <img
        alt=""
        className={twMerge(
          'absolute inset-0 w-full h-full object-cover object-top pointer-events-none',
          smallImageDisplayClassName,
          imageClassName,
          smallImageClassName
        )}
        src={`${hostname}/${pathname}/${filename}-small.${ext}`}
      />
      <img
        alt=""
        className={twMerge(
          'absolute inset-0 w-full h-full object-cover object-top pointer-events-none',
          largeImageDisplayClassName,
          imageClassName,
          largeImageClassName
        )}
        src={`${hostname}/${pathname}/${filename}-large.${ext}`}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export const PitchBackground = BasePitchBackground;
